import { ZuiBaseElement } from '@zywave/zui-base';
import { findAssignedElement } from '@zywave/zui-base/dist/utils/find-assigned-element.js';
import { html } from 'lit';
import { query, property } from 'lit/decorators.js';
import { style } from './zui-breadcrumb-css.js';
import '@zywave/zui-icons';
import type { PropertyValues } from 'lit';

/**
 * `<zui-breadcrumb>`s are passed as a direct descendant into the wrapper `<zui-breadcrumbs>`. `<zui-breadcrumb>` will accept and format correct styling for `<a>`, `<button>`, and other slotted elements. Choose your slotted element based upon your needs. If items can be opened in a new tab, we suggest using `<a>`.
 *
 * @element zui-breadcrumb
 *
 * @slot - Default, unnamed slot; for inserting `<div>`, `<a>`, or `<button>` elements into `<zui-breadcrumb>`
 */
export class ZuiBreadcrumb extends ZuiBaseElement {
  @query('slot') private _slotEl: HTMLSlotElement | undefined;

  /**
   * Apply attribute if you'd like to remove max character ellipsis on a single `zui-breadcrumb`
   */
  @property({ type: Boolean, attribute: 'expand-text', reflect: true })
  expandText = false;

  protected get _slottedControl() {
    return findAssignedElement(this._slotEl, 'a,button,div');
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'button');

    const clickKeys = [13, 32];
    this.addEventListener('keydown', (e: KeyboardEvent) => {
      if (clickKeys.includes(e.keyCode)) {
        this.focus();
        e.preventDefault();
        e.stopPropagation();
      }
    });

    this.addEventListener('keyup', (e: KeyboardEvent) => {
      if (clickKeys.includes(e.keyCode)) {
        this.click();
        e.preventDefault();
        e.stopPropagation();
      }
    });

    this.addEventListener('click', (e: KeyboardEvent) => {
      if (this._slottedControl && !e.composedPath().includes(this._slottedControl)) {
        this._slottedControl.click();
      }
    });
  }

  private _setTabIndex() {
    if (this._slottedControl) {
      this._slottedControl.setAttribute('tabindex', '-1');
      this.tabIndex = 0;
    } else {
      this.tabIndex = 0;
    }
  }

  static get styles() {
    return [super.styles, style];
  }

  protected firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);
    this._setTabIndex();
  }

  render() {
    return html`
      <zui-icon icon="zui-left"></zui-icon>
      <slot></slot>
    `;
  }
}

window.customElements.define('zui-breadcrumb', ZuiBreadcrumb);

declare global {
  interface HTMLElementTagNameMap {
    'zui-breadcrumb': ZuiBreadcrumb;
  }
}
